// About — feature tour for aero.chat.
//
// Full-height parallax hero over a live wake, then one section per
// product surface with an animated storyboard. Reveal-on-scroll via
// IntersectionObserver, parallax on hero + section backgrounds via
// a scroll listener (no external animation library).

import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Container, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageLayout from './components/PageLayout';
import {
  AeroAiScene,
  AnalyticsScene,
  DropsTeaserScene,
  FeedScene,
  FocusScene,
  IntensityScene,
  LaptopFrame,
  PhoneFrame,
  RecordingScene,
  WakeMini,
  WeatherScene,
} from './components/AboutStoryboards';
import { colors } from './theme';

// -----------------------------------------------------------------
// Hooks
// -----------------------------------------------------------------

function useScrollY(): number {
  const [y, setY] = useState(0);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setY(window.scrollY);
        raf = 0;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return y;
}

function Reveal({
  children,
  y = 32,
  delayMs = 0,
}: {
  children: React.ReactNode;
  y?: number;
  delayMs?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : `translateY(${y}px)`,
        transition: `opacity 700ms ease ${delayMs}ms, transform 700ms cubic-bezier(.2,.7,.2,1) ${delayMs}ms`,
      }}
    >
      {children}
    </Box>
  );
}

// -----------------------------------------------------------------
// Feature section — copy left, storyboard right (or reversed)
// -----------------------------------------------------------------

function FeatureSection({
  eyebrow,
  title,
  body,
  storyboard,
  reversed,
  tint,
  cta,
}: {
  eyebrow: string;
  title: string;
  body: string;
  storyboard: React.ReactNode;
  reversed?: boolean;
  tint?: string;
  cta?: { label: string; to: string; external?: boolean };
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 8, md: 14 },
        px: { xs: 3, md: 4 },
        overflow: 'hidden',
        background: tint
          ? `radial-gradient(900px 500px at ${reversed ? '20%' : '80%'} 40%, ${tint}, transparent 70%)`
          : 'transparent',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: reversed ? '1fr 1fr' : '1fr 1fr' },
            gap: { xs: 5, md: 8 },
            alignItems: 'center',
            direction: reversed ? 'rtl' : 'ltr',
          }}
        >
          <Box sx={{ direction: 'ltr' }}>
            <Reveal>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  letterSpacing: '0.22em',
                  color: colors.brand,
                  mb: 1.5,
                }}
              >
                {eyebrow}
              </Typography>
            </Reveal>
            <Reveal delayMs={80}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  fontWeight: 900,
                  letterSpacing: '-0.02em',
                  color: colors.text,
                  lineHeight: 1.1,
                  mb: 2.5,
                }}
              >
                {title}
              </Typography>
            </Reveal>
            <Reveal delayMs={160}>
              <Typography
                sx={{
                  fontSize: '1.05rem',
                  color: colors.textMuted,
                  lineHeight: 1.65,
                  maxWidth: 520,
                }}
              >
                {body}
              </Typography>
            </Reveal>
            {cta && (
              <Reveal delayMs={240}>
                <Button
                  component={cta.external ? 'a' : RouterLink}
                  {...(cta.external ? { href: cta.to } : { to: cta.to })}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    mt: 3,
                    color: colors.brand,
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    px: 0,
                    '&:hover': { color: colors.brandBright, background: 'transparent' },
                  }}
                >
                  {cta.label}
                </Button>
              </Reveal>
            )}
          </Box>
          <Box sx={{ direction: 'ltr', display: 'flex', justifyContent: 'center' }}>
            <Reveal delayMs={120} y={40}>
              {storyboard}
            </Reveal>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

// -----------------------------------------------------------------
// Page
// -----------------------------------------------------------------

export const About = (_props: { theme?: any }) => {
  const { t } = useTranslation('about');
  const scrollY = useScrollY();

  // Hero wake — a live iframe of /wake-embed with a hardcoded set
  // of five positions so a first-time visitor sees a fully-formed
  // wake regardless of whether they have any sessions.
  const heroWakeSrc =
    '/wake-embed?avgCda=0.26&positions=aero_tuck:0.20:0.008:5,tt:0.22:0.030:3,drops:0.25:0.020:8,hoods:0.30:0.012:6,seated:0.38:0.045:4';

  return (
    <PageLayout fullBleed>
      <Box sx={{ backgroundColor: colors.bg, color: colors.text }}>
        {/* ============================================================
            HERO — full viewport with wake as parallax background
            ============================================================ */}
        <Box
          sx={{
            position: 'relative',
            minHeight: '100vh',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Parallax wake layer — translates slower than scroll for
              depth. Sized 120vh so the parallax offset doesn't reveal
              the page background at the bottom. */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '120vh',
              transform: `translateY(${scrollY * 0.35}px)`,
              willChange: 'transform',
            }}
          >
            <Box
              component="iframe"
              src={heroWakeSrc}
              title="Aero Wake"
              sx={{
                width: '100%',
                height: '100%',
                border: 0,
                display: 'block',
                // Wave 20.49ar — Reader-first: dim the busy wake
                // (chips + streaks were bleeding into the hero
                // copy on mobile). Rendered mostly for
                // atmospheric wash rather than as the focal
                // subject.
                opacity: { xs: 0.35, md: 0.55 },
              }}
            />
            {/* Wave 20.49ar — Radial vignette that darkens the
                center where the hero copy sits, so the wake
                becomes atmospheric rather than fighting the
                text. Preserves the "wake as backdrop" feel
                without the readability cost. */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: {
                  xs: `radial-gradient(120% 90% at 30% 45%, ${colors.bg}f0 0%, ${colors.bg}c8 45%, transparent 85%), linear-gradient(180deg, transparent 0%, ${colors.bg}88 55%, ${colors.bg} 100%)`,
                  md: `radial-gradient(90% 80% at 30% 50%, ${colors.bg}d8 0%, ${colors.bg}a0 45%, transparent 85%), linear-gradient(180deg, transparent 0%, ${colors.bg}00 40%, ${colors.bg}cc 82%, ${colors.bg} 100%)`,
                },
                pointerEvents: 'none',
              }}
            />
          </Box>

          {/* Hero copy — foreground, parallax'd faster than the wake
              so it feels like it sits above. */}
          <Container
            maxWidth="lg"
            sx={{
              position: 'relative',
              zIndex: 1,
              py: { xs: 6, md: 10 },
              transform: `translateY(${scrollY * -0.05}px)`,
            }}
          >
            <Box sx={{ maxWidth: 780 }}>
              <Chip
                label={t('hero.chip')}
                size="small"
                sx={{
                  backgroundColor: colors.brand,
                  color: '#0A100C',
                  fontWeight: 800,
                  letterSpacing: '0.16em',
                  mb: 2.5,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.75rem', sm: '3.5rem', md: '5rem' },
                  fontWeight: 900,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.02,
                  mb: 3,
                }}
              >
                {t('hero.title')}
                <br />
                <Box component="span" sx={{ color: colors.brand }}>
                  {t('hero.titleAccent')}
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.35rem' },
                  color: colors.textMuted,
                  lineHeight: 1.55,
                  maxWidth: 620,
                  mb: 4,
                }}
              >
                {t('hero.subtitle')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  sx={{
                    backgroundColor: colors.brand,
                    color: '#0A100C',
                    fontWeight: 800,
                    letterSpacing: '0.03em',
                    px: 4,
                    py: 1.75,
                    fontSize: '1rem',
                    boxShadow: `0 20px 48px ${colors.brand}44`,
                    '&:hover': { backgroundColor: colors.brandBright },
                  }}
                >
                  {t('hero.cta')}
                </Button>
                <Button
                  href="#tour"
                  variant="outlined"
                  size="large"
                  sx={{
                    color: colors.text,
                    borderColor: colors.border,
                    px: 4,
                    py: 1.75,
                    fontSize: '1rem',
                    '&:hover': { borderColor: colors.brand, color: colors.brand },
                  }}
                >
                  {t('hero.secondary')}
                </Button>
              </Stack>
              {/* Scroll cue */}
              <Box
                sx={{
                  mt: { xs: 6, md: 10 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  color: colors.textMuted,
                  fontSize: '0.7rem',
                  letterSpacing: '0.18em',
                  fontWeight: 700,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 1.5,
                    backgroundColor: colors.brand,
                    animation: 'scrollHint 2s ease-in-out infinite',
                    '@keyframes scrollHint': {
                      '0%, 100%': { transform: 'scaleX(0.4)', transformOrigin: 'left' },
                      '50%': { transform: 'scaleX(1)', transformOrigin: 'left' },
                    },
                  }}
                />
                {t('hero.scrollHint')}
              </Box>
            </Box>
          </Container>
        </Box>

        <Box id="tour" />

        {/* ============================================================
            AERO AI
            ============================================================ */}
        <FeatureSection
          eyebrow={t('sections.aero.eyebrow')}
          title={t('sections.aero.title')}
          body={t('sections.aero.body')}
          storyboard={
            <LaptopFrame>
              <AeroAiScene />
            </LaptopFrame>
          }
          tint={`${colors.brand}0f`}
          cta={{ label: t('sections.aero.cta'), to: '/session' }}
        />

        {/* ============================================================
            WAKE
            ============================================================ */}
        <FeatureSection
          eyebrow={t('sections.wake.eyebrow')}
          title={t('sections.wake.title')}
          body={t('sections.wake.body')}
          storyboard={<WakeMini />}
          reversed
          tint={`${colors.brand}0a`}
          cta={{ label: t('sections.wake.cta'), to: '/wake' }}
        />

        {/* ============================================================
            FOCUS
            ============================================================ */}
        <FeatureSection
          eyebrow={t('sections.focus.eyebrow')}
          title={t('sections.focus.title')}
          body={t('sections.focus.body')}
          storyboard={
            <PhoneFrame accent size="md">
              <FocusScene />
            </PhoneFrame>
          }
          tint={`${colors.brand}0f`}
        />

        {/* ============================================================
            OUTDOOR + PEAKS
            ============================================================ */}
        <FeatureSection
          eyebrow={t('sections.outdoor.eyebrow')}
          title={t('sections.outdoor.title')}
          body={t('sections.outdoor.body')}
          storyboard={
            <PhoneFrame accent size="md">
              <RecordingScene />
            </PhoneFrame>
          }
          reversed
          tint={`${colors.brand}0f`}
        />

        {/* ============================================================
            WEATHER + WIND
            ============================================================ */}
        <FeatureSection
          eyebrow={t('sections.weather.eyebrow')}
          title={t('sections.weather.title')}
          body={t('sections.weather.body')}
          storyboard={<WeatherScene />}
          tint={'rgba(90,168,255,0.08)'}
        />

        {/* ============================================================
            AERO INTENSITY
            ============================================================ */}
        <FeatureSection
          eyebrow={t('sections.intensity.eyebrow')}
          title={t('sections.intensity.title')}
          body={t('sections.intensity.body')}
          storyboard={<IntensityScene />}
          reversed
          tint={`${colors.brand}0a`}
        />

        {/* ============================================================
            FEED
            ============================================================ */}
        <FeatureSection
          eyebrow={t('sections.feed.eyebrow')}
          title={t('sections.feed.title')}
          body={t('sections.feed.body')}
          storyboard={<FeedScene />}
          reversed
          tint={`${colors.brand}0a`}
          cta={{ label: t('sections.feed.cta'), to: '/feed' }}
        />

        {/* ============================================================
            DROPS — teaser + link to full /drops page
            ============================================================ */}
        <FeatureSection
          eyebrow={t('sections.drops.eyebrow')}
          title={t('sections.drops.title')}
          body={t('sections.drops.body')}
          storyboard={
            <PhoneFrame accent size="md">
              <DropsTeaserScene />
            </PhoneFrame>
          }
          tint={`${colors.brand}0f`}
          cta={{ label: t('sections.drops.cta'), to: '/drops' }}
        />

        {/* ============================================================
            ANALYTICS
            ============================================================ */}
        <FeatureSection
          eyebrow={t('sections.analytics.eyebrow')}
          title={t('sections.analytics.title')}
          body={t('sections.analytics.body')}
          storyboard={<AnalyticsScene />}
          reversed
          tint={`${colors.brand}0a`}
          cta={{ label: t('sections.analytics.cta'), to: '/dashboard/aero' }}
        />

        {/* ============================================================
            BIO
            ============================================================ */}
        <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 3, md: 4 } }}>
          <Container maxWidth="md">
            <Reveal>
              <Box
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  backgroundColor: colors.surface,
                  border: `1px solid ${colors.borderFocus}`,
                  boxShadow: `0 24px 60px rgba(0,0,0,0.4)`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 800,
                    letterSpacing: '0.22em',
                    color: colors.brand,
                    mb: 1.5,
                  }}
                >
                  {t('bio.title')}
                </Typography>
                <Typography
                  sx={{
                    color: colors.text,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    lineHeight: 1.7,
                  }}
                >
                  {t('bio.body')}
                </Typography>
              </Box>
            </Reveal>
          </Container>
        </Box>

        {/* ============================================================
            FINAL CTA
            ============================================================ */}
        <Box
          sx={{
            py: { xs: 10, md: 16 },
            px: { xs: 3, md: 4 },
            textAlign: 'center',
            background: `radial-gradient(1400px 500px at 50% 60%, ${colors.brand}22, transparent 70%)`,
          }}
        >
          <Container maxWidth="md">
            <Reveal>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.25rem', md: '3.5rem' },
                  fontWeight: 900,
                  letterSpacing: '-0.03em',
                  mb: 2,
                  lineHeight: 1.05,
                }}
              >
                {t('cta.title')}
              </Typography>
            </Reveal>
            <Reveal delayMs={120}>
              <Typography sx={{ color: colors.textMuted, fontSize: '1.15rem', mb: 4, maxWidth: 620, mx: 'auto' }}>
                {t('cta.body')}
              </Typography>
            </Reveal>
            <Reveal delayMs={200}>
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                size="large"
                startIcon={<PlayArrowIcon />}
                sx={{
                  backgroundColor: colors.brand,
                  color: '#0A100C',
                  fontWeight: 800,
                  letterSpacing: '0.03em',
                  px: 5,
                  py: 2,
                  fontSize: '1.05rem',
                  boxShadow: `0 24px 64px ${colors.brand}55`,
                  '&:hover': { backgroundColor: colors.brandBright },
                }}
              >
                {t('cta.button')}
              </Button>
            </Reveal>
          </Container>
        </Box>
      </Box>
    </PageLayout>
  );
};
